import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import {
  CommunityGroup,
  CommunityGroupsState,
  CreateCommunityGroupRequest,
  UpdateCommunityGroupRequest,
  FilterCommunityGroupParams,
  JoinGroupRequest,
  LeaveGroupRequest,
  AddMemberRequest,
  CommunityGroupsListResponse,
  CommunityGroupResponse,
  ExportResponse,
  RedisPostResponse,
  CommunityGroupsApiError,
} from './types';

const initialState: CommunityGroupsState = {
  groups: [],
  currentGroup: null,
  adminGroups: [],
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  exportData: null,
};

// API base URL - adjust according to your backend URL
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Create community group
export const createCommunityGroup = createAsyncThunk<
  CommunityGroup,
  CreateCommunityGroupRequest,
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/createCommunityGroup', async (groupData, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/community-groups`, groupData);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to create community group',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Get community groups by admin ID
export const getCommunityGroupsByAdminId = createAsyncThunk<
  CommunityGroup[],
  void,
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/getCommunityGroupsByAdminId', async (_, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/community-groups/admin`);
    // The API returns double-nested structure: { success: true, data: { success: true, data: [...], message: "..." } }
    return response.data.data.data;
  } catch (error: any) {
    console.error('API call: Error occurred', error);
    console.error('API call: Error response', error.response);
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to fetch admin community groups',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Get all community groups with filters
export const getCommunityGroups = createAsyncThunk<
  CommunityGroupsListResponse,
  FilterCommunityGroupParams,
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/getCommunityGroups', async (params, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.type) queryParams.append('type', params.type);
    if (params.visibility) queryParams.append('visibility', params.visibility);
    if (params.status) queryParams.append('status', params.status);
    if (params.locationName) queryParams.append('locationName', params.locationName);
    if (params.pincode) queryParams.append('pincode', params.pincode);
    if (params.adminId) queryParams.append('adminId', params.adminId);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await axios.get(`${API_BASE_URL}/community-groups?${queryParams}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to fetch community groups',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Get community group by ID
export const getCommunityGroupById = createAsyncThunk<
  CommunityGroup,
  string,
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/getCommunityGroupById', async (id, { rejectWithValue }) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/community-groups/${id}`);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to fetch community group',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Update community group
export const updateCommunityGroup = createAsyncThunk<
  CommunityGroup,
  { id: string; updateData: UpdateCommunityGroupRequest },
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/updateCommunityGroup', async ({ id, updateData }, { rejectWithValue }) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/community-groups/${id}`, updateData);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to update community group',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Delete community group
export const deleteCommunityGroup = createAsyncThunk<
  void,
  string,
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/deleteCommunityGroup', async (id, { rejectWithValue }) => {
  try {
    await axios.delete(`${API_BASE_URL}/community-groups/${id}`);
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to delete community group',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Add member to community group
export const addMember = createAsyncThunk<
  CommunityGroup,
  { id: string; memberData: AddMemberRequest },
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/addMember', async ({ id, memberData }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/community-groups/${id}/members`, memberData);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to add member',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Remove member from community group
export const removeMember = createAsyncThunk<
  CommunityGroup,
  { id: string; memberId: string },
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/removeMember', async ({ id, memberId }, { rejectWithValue }) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/community-groups/${id}/members/${memberId}`);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to remove member',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Join community group
export const joinGroup = createAsyncThunk<
  CommunityGroup,
  { id: string; joinData: JoinGroupRequest },
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/joinGroup', async ({ id, joinData }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/community-groups/${id}/join`, joinData);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to join group',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Leave community group
export const leaveGroup = createAsyncThunk<
  void,
  { id: string; leaveData: LeaveGroupRequest },
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/leaveGroup', async ({ id, leaveData }, { rejectWithValue }) => {
  try {
    await axios.post(`${API_BASE_URL}/community-groups/${id}/leave`, leaveData);
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to leave group',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Export community groups
export const exportGroups = createAsyncThunk<
  string,
  { format: 'csv' | 'excel'; params: FilterCommunityGroupParams },
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/exportGroups', async ({ format, params }, { rejectWithValue }) => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.type) queryParams.append('type', params.type);
    if (params.visibility) queryParams.append('visibility', params.visibility);
    if (params.status) queryParams.append('status', params.status);
    if (params.locationName) queryParams.append('locationName', params.locationName);
    if (params.pincode) queryParams.append('pincode', params.pincode);
    if (params.adminId) queryParams.append('adminId', params.adminId);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await axios.get(`${API_BASE_URL}/community-groups/export/${format}?${queryParams}`);
    return response.data.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to export groups',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

// Post groups list to Redis
export const postGroupsListToRedis = createAsyncThunk<
  RedisPostResponse,
  { page?: number; limit?: number },
  { rejectValue: CommunityGroupsApiError }
>('communityGroups/postGroupsListToRedis', async ({ page = 1, limit = 10 }, { rejectWithValue }) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/community-groups/redis/post-groups-list?page=${page}&limit=${limit}`);
    return response.data;
  } catch (error: any) {
    return rejectWithValue({
      message: error.response?.data?.message || 'Failed to post groups list to Redis',
      statusCode: error.response?.status || 500,
      error: error.response?.data?.error,
    });
  }
});

const communityGroupsSlice = createSlice({
  name: 'communityGroups',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentGroup: (state) => {
      state.currentGroup = null;
    },
    clearGroups: (state) => {
      state.groups = [];
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      };
    },
    clearExportData: (state) => {
      state.exportData = null;
    },
    setPagination: (state, action: PayloadAction<{ page: number; limit: number; total: number; totalPages: number }>) => {
      state.pagination = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Create community group
    builder
      .addCase(createCommunityGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCommunityGroup.fulfilled, (state, action) => {
        state.loading = false;
        state.groups.unshift(action.payload);
        state.pagination.total += 1;
      })
      .addCase(createCommunityGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to create community group';
      });

    // Get community groups by admin ID
    builder
      .addCase(getCommunityGroupsByAdminId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCommunityGroupsByAdminId.fulfilled, (state, action) => {
        state.loading = false;
        state.adminGroups = action.payload;
      })
      .addCase(getCommunityGroupsByAdminId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch admin community groups';
      });

    // Get all community groups
    builder
      .addCase(getCommunityGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCommunityGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload.data.groups;
        state.pagination = {
          page: action.payload.data.page,
          limit: action.payload.data.limit,
          total: action.payload.data.total,
          totalPages: Math.ceil(action.payload.data.total / action.payload.data.limit),
        };
      })
      .addCase(getCommunityGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch community groups';
      });

    // Get community group by ID
    builder
      .addCase(getCommunityGroupById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCommunityGroupById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentGroup = action.payload;
      })
      .addCase(getCommunityGroupById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to fetch community group';
      });

    // Update community group
    builder
      .addCase(updateCommunityGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCommunityGroup.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(group => group._id === action.payload._id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        if (state.currentGroup?._id === action.payload._id) {
          state.currentGroup = action.payload;
        }
        // Update admin groups if it exists there
        const adminIndex = state.adminGroups.findIndex(group => group._id === action.payload._id);
        if (adminIndex !== -1) {
          state.adminGroups[adminIndex] = action.payload;
        }
      })
      .addCase(updateCommunityGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to update community group';
      });

    // Delete community group
    builder
      .addCase(deleteCommunityGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCommunityGroup.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the deleted group from all arrays
        state.groups = state.groups.filter(group => group._id !== action.meta.arg);
        state.adminGroups = state.adminGroups.filter(group => group._id !== action.meta.arg);
        if (state.currentGroup?._id === action.meta.arg) {
          state.currentGroup = null;
        }
        state.pagination.total -= 1;
      })
      .addCase(deleteCommunityGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to delete community group';
      });

    // Add member
    builder
      .addCase(addMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMember.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(group => group._id === action.payload._id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        if (state.currentGroup?._id === action.payload._id) {
          state.currentGroup = action.payload;
        }
        // Update admin groups if it exists there
        const adminIndex = state.adminGroups.findIndex(group => group._id === action.payload._id);
        if (adminIndex !== -1) {
          state.adminGroups[adminIndex] = action.payload;
        }
      })
      .addCase(addMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to add member';
      });

    // Remove member
    builder
      .addCase(removeMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeMember.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(group => group._id === action.payload._id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        if (state.currentGroup?._id === action.payload._id) {
          state.currentGroup = action.payload;
        }
        // Update admin groups if it exists there
        const adminIndex = state.adminGroups.findIndex(group => group._id === action.payload._id);
        if (adminIndex !== -1) {
          state.adminGroups[adminIndex] = action.payload;
        }
      })
      .addCase(removeMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to remove member';
      });

    // Join group
    builder
      .addCase(joinGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(joinGroup.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.groups.findIndex(group => group._id === action.payload._id);
        if (index !== -1) {
          state.groups[index] = action.payload;
        }
        if (state.currentGroup?._id === action.payload._id) {
          state.currentGroup = action.payload;
        }
        // Update admin groups if it exists there
        const adminIndex = state.adminGroups.findIndex(group => group._id === action.payload._id);
        if (adminIndex !== -1) {
          state.adminGroups[adminIndex] = action.payload;
        }
      })
      .addCase(joinGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to join group';
      });

    // Leave group
    builder
      .addCase(leaveGroup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(leaveGroup.fulfilled, (state, action) => {
        state.loading = false;
        // Update the group in the lists to reflect the user leaving
        const groupId = action.meta.arg.id;
        const index = state.groups.findIndex(group => group._id === groupId);
        if (index !== -1) {
          // Remove the user from members array
          const userId = action.meta.arg.leaveData.userId;
          state.groups[index].members = state.groups[index].members.filter(member => member._id !== userId);
          state.groups[index].memberCount = state.groups[index].members.length;
        }
        if (state.currentGroup?._id === groupId) {
          const userId = action.meta.arg.leaveData.userId;
          state.currentGroup.members = state.currentGroup.members.filter(member => member._id !== userId);
          state.currentGroup.memberCount = state.currentGroup.members.length;
        }
        // Update admin groups if it exists there
        const adminIndex = state.adminGroups.findIndex(group => group._id === groupId);
        if (adminIndex !== -1) {
          const userId = action.meta.arg.leaveData.userId;
          state.adminGroups[adminIndex].members = state.adminGroups[adminIndex].members.filter(member => member._id !== userId);
          state.adminGroups[adminIndex].memberCount = state.adminGroups[adminIndex].members.length;
        }
      })
      .addCase(leaveGroup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to leave group';
      });

    // Export groups
    builder
      .addCase(exportGroups.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(exportGroups.fulfilled, (state, action) => {
        state.loading = false;
        state.exportData = action.payload;
      })
      .addCase(exportGroups.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to export groups';
      });

    // Post groups list to Redis
    builder
      .addCase(postGroupsListToRedis.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(postGroupsListToRedis.fulfilled, (state) => {
        state.loading = false;
        // Successfully posted to Redis
      })
      .addCase(postGroupsListToRedis.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || 'Failed to post groups list to Redis';
      });
  },
});

export const { clearError, clearCurrentGroup, clearGroups, clearExportData, setPagination } = communityGroupsSlice.actions;

export default communityGroupsSlice.reducer;
